import React, { useEffect, useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Input,
  Form,
  FormGroup,
  Label,
  UncontrolledTooltip,
} from "reactstrap";
import { Link } from "react-router-dom";
import Breadcrumbs from "components/Common/Breadcrumb";
import TableContainer from "components/Common/TableContainer";
import CustomDatePicker from "common/CustomDatePicker";
import CustomSelect from "components/Common/custom-select";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import { useAuthUserDetails } from "components/Common/getAuthUserDetails";
import { changeExpensesHeadStatus, fetchExpensesHeads, fetchReimbursementHeads, saveExpensesHead, fetchExpensesMappedGridheads } from "store/actions";
import Swal from "sweetalert2";
import Loader from "common/pageLoader";
import { showCancelConfirmation } from "common/Confirmationmessage";

const ExpenseMasterHeads = () => {
  const dispatch = useDispatch();
  const [authDetails, setAuthDetails] = useState(null);
  const {
    masterheads,
    mappedGridheads,
    loading,
    success,
    error,
  } = useSelector((state) => ({
    masterheads: state.ExpensesMasterReducer?.masterheads ?? [],
    mappedGridheads: state.ExpensesMasterReducer?.mappedGridheads ?? [],
    loading: state.ExpensesMasterReducer?.loading,
    error: state.ExpensesMasterReducer?.error,
    success: state.ExpensesMasterReducer?.success,
  }));

  useEffect(() => {
    const authUserDetails = useAuthUserDetails();
    setAuthDetails(authUserDetails[0])
    const unitId = authUserDetails[0].unit_id
    dispatch(fetchExpensesHeads(unitId));
    dispatch(fetchExpensesMappedGridheads(unitId));
  }, [dispatch]);

  useEffect(() => {
    if (success) {
      dispatch(fetchExpensesHeads(authDetails?.unit_id));
      dispatch(fetchExpensesMappedGridheads(authDetails?.unit_id));
      formik.resetForm();
      setIsEdit(false);
      setEditId(null);
    }
  }, [authDetails, success, dispatch]);

  const [isEdit, setIsEdit] = useState(false);
  const [editId, setEditId] = useState(null);

  const formik = useFormik({
    initialValues: {
      headName: "",
    },
    validationSchema: Yup.object({
      headName: Yup.string().trim().required("Heads Name is required").test("no-whitespace-only", "Heads Name is required", val => val && val.trim().replace(/\s+/g, "") !== "").max(100, "Head Name cannot exceed 100 Characters"),
    }),
    onSubmit: (values, { resetForm }) => {
      const formData = new FormData();
      formData.append("expenseshead_id", editId ? editId : 0);
      formData.append("expensesheads_name", values.headName.trim());
      formData.append("unit_id", authDetails?.unit_id);
      formData.append("create_by", authDetails?.user_id);
      dispatch(saveExpensesHead(formData))
    },
  });

  const handleEdit = (row) => {
    formik.setValues({
      headName: row.expense_name,
    });
    setIsEdit(true);
    setEditId(row.exp_id);
  };

  const handleStatusToggle = (row) => {
    setTableData((prev) =>
      prev.map((item) =>
        item.id === row.id ? { ...item, status: item.status ? 0 : 1 } : item
      )
    );
  };


  const columns = [
    {
      Header: "S.No",
      Cell: ({ row }) => mappedGridheads.length - row.index,
      className: "serial-no text-center",
    },

     {
      Header: "Action",
      className: "action-col",
      applyHeaderClass: true,
      Cell: (cellProps) => {
        const { b_active, exp_id } = cellProps.row.original;
        const isActive = b_active === 1;
        return (
          <div className="d-flex gap-3">
            <Link
              to="#"
              style={{ color: isActive ? "green" : "red" }}
              onClick={() => handleToggleStatus(exp_id, isActive)}
            >
              <i
                className={`mdi ${isActive ? "mdi-check-circle" : "mdi-close-circle"} font-size-18`}
                id={`status-tooltip-${exp_id}`}
              />
              <UncontrolledTooltip placement="top" target={`status-tooltip-${exp_id}`}>
                {isActive ? "Mark Inactive" : "Mark Active"}
              </UncontrolledTooltip>
            </Link>
            <Link
              to="#"
              className="text-success"
              onClick={() => handleEdit(cellProps.row.original)}
            >
              <i className="bx bxs-edit  font-size-18" id={`edittooltip-${exp_id}`} />
              <UncontrolledTooltip placement="top" target={`edittooltip-${exp_id}`}>
                Edit
              </UncontrolledTooltip>
            </Link>
          </div>
        );
      },
    },
    
    {
      Header: "Heads Name",
      accessor: "expense_name",
    },
   
  ];

  const handleToggleStatus = (id, isActive) => {
    Swal.fire({
      title: 'Are you sure?',
      text: `You are about to ${isActive ? 'deactivate' : 'activate'} this.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Confirm',
      cancelButtonText: 'Cancel',
      reverseButtons: false,
      dangerMode: true,
    }).then((result) => {
      if (result.isConfirmed) {
        const formData = new FormData();
        formData.append("expenseshead_id", id);
        formData.append("create_by", authDetails?.user_id);
        dispatch(changeExpensesHeadStatus(formData));
      }
    });
  };

  const handleReset = () => {
    if (formik.dirty || isEdit) {
      showCancelConfirmation(() => {
        formik.resetForm();
        setIsEdit(false);
        setEditId(null);
      });
    } else {
      formik.resetForm();
      setIsEdit(false);
      setEditId(null);
    }
  };

  return (
    <div className="page-content">
      <Loader loading={loading} />
      <Container fluid>
        <Breadcrumbs title="Expenses" breadcrumbItem="Heads" />

        <Card>
          <CardBody>
            <Form onSubmit={formik.handleSubmit}>
              <Row className="mb-3">
                <Col md={4}>
                  <FormGroup>
                    <Label for="headName">
                      Heads Name <span className="text-danger">*</span>
                    </Label>
                    <Input
                      id="headName"
                      name="headName"
                      type="text"
                      value={formik.values.headName}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      invalid={(formik.touched.headName && Boolean(formik.errors.headName)) || formik.values.headName.length > 100}
                    />
                    {formik.values.headName.length > 100 ? (
                      <div className="text-danger">Head Name cannot exceed 100 Characters</div>
                    ) : formik.touched.headName && formik.errors.headName ? (
                      <div className="text-danger">{formik.errors.headName}</div>
                    ) : null}
                  </FormGroup>
                </Col>
                <Col md="8" className="d-flex mob-center align-items-center justify-content-end mt-4">
                  <Button type="submit" className="btn button-save me-2">
                    <i className="fas fa-save me-1" /> {isEdit ? "Update" : "Save"}
                  </Button>
                  <Button
                    type="button"
                    onClick={handleReset}
                    className="btn button-cancel"> <i className="fas fa-sync me-1" /> Cancel
                  </Button>
                </Col>
              </Row>
            </Form>
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <TableContainer
              columns={columns}
              data={mappedGridheads}
              isGlobalFilter={true}
              customPageSize={10}
              tableSorter={true}
            />
          </CardBody>
        </Card>
      </Container>
    </div>
  );
};

export default ExpenseMasterHeads;
